import axios, {AxiosRequestConfig, AxiosResponse} from 'axios'
import {getIdToken} from 'firebase/auth'
import {auth} from '../../../App'
import {API_BASE_URL} from '../../../constants'
import {signOutFromFirebase} from '../../../firebase/firebaseSetup'
import {HttpStatusCode, RequestMethod} from '../../../model/enums/enums'
import {State} from '../../../model/state.model'

// counter to identify api request/response in log
let apiCounter = 0

export const handleMediaUpload = async (
  requestUrl: string,
  body: any,
  token: string,
  fileName: any
) => {
  const fileData = new FormData()
  if (fileName) {
    fileData.append(fileName, body)
  }
  return await axios.post(requestUrl, fileData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'content-type': 'multipart/form-data',
    },
  })
}

/**
 * Handle any axios request
 * @param {string} url, the provided url can be suffix for requests
 * against mokai-backend or full url for external requests
 * @param {RequestMethod} method, the HttpRequest method
 * @param body, optional body for put and post requests
 * @param fileName
 * @param {boolean} isCompleteUrl, indicator if url is suffix or an entire url containing https etc.
 * and base does not need to be added
 * @returns {Promise<State<T>>}
 */
export async function handleRequest<T>(
  url: string,
  method: RequestMethod,
  body?: any,
  fileName?: string | undefined,
  isCompleteUrl = false
): Promise<State<T>> {
  apiCounter++

  const initialState: State<T> = {
    httpStatus: 0,
    error: undefined,
    data: undefined,
    loading: true,
  }

  let requestUrl = url
  if (!isCompleteUrl) {
    requestUrl = API_BASE_URL + url
  }

  // copy current value for response
  const activeRequestNumber = apiCounter

  if (!auth?.currentUser) {
    return initialState
  }

  try {
    // ref: https://firebase.google.com/docs/reference/js/v8/firebase.User#getidtoken
    const token = await getIdToken(auth?.currentUser, false)

    // If the token is invalid return
    if (!token) {
      initialState.loading = false
      initialState.error = new Error('Token missing')
      console.error(
        '[Request] Could not get the user token, can not query the server.'
      )
      return initialState
    }

    const config: AxiosRequestConfig = {
      headers: { Authorization: `Bearer ${token}` },
    }

    let response: AxiosResponse

    console.log(
      `[REQUEST (${activeRequestNumber})] - ${url} - sent (${RequestMethod[method]}).`
    )

    switch (method) {
      case RequestMethod.GET:
        response = await axios.get(requestUrl, config)
        break
      case RequestMethod.GET_PDF:
        config.responseType = 'arraybuffer'
        response = await axios.get(requestUrl, config)
        break
      case RequestMethod.POST: // add
        if (body) {
          response = await axios.post(requestUrl, body, config)
        } else {
          response = await axios.post(requestUrl, {}, config)
        }
        break
      case RequestMethod.PUT: // update
        response = await axios.put(requestUrl, body, config)
        break
      case RequestMethod.DELETE:
        response = await axios.delete(requestUrl, config)
        break
      case RequestMethod.UPLOAD:
        // we do not need to send the token but we need to change the app-header for file uploads
        response = await axios.put(requestUrl, body, {
          headers: {
            'Content-Type': 'application/octet-stream',
          },
        })
        break
      case RequestMethod.UPLOAD_MEDIA:
        // we need to change the app-header for file uploads
        response = await handleMediaUpload(requestUrl, body, token, fileName)
        break
      default:
        console.error(`[REQUEST (${activeRequestNumber})] 
            - Unhandled RequestMethod provided. No request fired!`)
        return {
          ...initialState,
          error: new Error('Unhandled request method provided'),
          loading: false,
        }
    }

    if (!response.status) {
      console.error(
        `[RESPONSE (${activeRequestNumber})] of '${requestUrl}' was invalid.`
      )
      const error = new Error(
        `[RESPONSE (${activeRequestNumber})] of '${requestUrl}' was invalid.`
      )
      return { ...initialState, error: error, loading: false }
    }

    const firstStatusDigit = response.status?.toString()?.substring(0, 1)

    // 2xx = always OK/successful
    if (firstStatusDigit === '2') {
      console.log(
        `[RESPONSE (${activeRequestNumber})] - ${url} - was successful. (${response?.data?.length})`
      )
      return {
        ...initialState,
        data: response?.data,
        httpStatus: response.status,
        loading: false,
      }
    }

    if (firstStatusDigit === '4') {
      // unauthorized
      if (response.status === HttpStatusCode.UNAUTHORIZED) {
        console.error(
          `[RESPONSE (${activeRequestNumber})] - ${url} - was unauthorized. Logging out.`
        )
        await signOutFromFirebase()
        const error = new Error(
          `[RESPONSE (${activeRequestNumber})] - ${url} - was unauthorized. New login required.`
        )
        return {
          ...initialState,
          error: error,
          httpStatus: response.status,
          loading: false,
        }
      }

      // forbidden
      if (response.status === HttpStatusCode.FORBIDDEN) {
        console.error(
          `[RESPONSE (${activeRequestNumber})] - ${url} - was forbidden. Logging out.`
        )
        await signOutFromFirebase()
        const error = new Error(
          `[RESPONSE (${activeRequestNumber})] - ${url} - was forbidden. New login required.`
        )

        return {
          ...initialState,
          error: error,
          httpStatus: response.status,
          loading: false,
        }
      }
    }

    // 5xx = always internal server error, the requesting ressource has to decide.
    if (firstStatusDigit === '5') {
      console.error(
        `[RESPONSE (${activeRequestNumber})] - ${url} - ended in an internal server error.`
      )
      return {
        ...initialState,
        error: new Error('500'),
        httpStatus: response?.status,
        loading: false,
      }
    }
  } catch (error: any) {
    console.error(error)
    return {
      ...initialState,
      error: error,
      httpStatus: error?.response?.status,
      loading: false,
    }
  }

  console.warn(
    `[RESPONSE (${activeRequestNumber})] - ${url} - triggered none of the handled cases!`
  )
  return { ...initialState, loading: false }
}